import Card from '@packages/ui/card/card.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import { useTranslate } from '@tolgee/react';
import CardContent from '@packages/ui/card/card-content.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import { useEffect, useState } from 'react';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import useUpdateStripeApi from '@client/page/management/stripe/stripe-api-key/logic/use-update-stripe-api';
import useDeleteStripeApi from '@client/page/management/stripe/stripe-api-key/logic/use-delete-stripe-api.ts';
import DestroyButton from '@packages/ui/button/destroy-button.tsx';
import useValidateStripeAPIKey from '../logic/use-validate-stripe-api-key';

interface ApiKeyInput {
  id: string;
  name: string;
  secretKey: string;
  publishableKey: string;
  webhookSecret?: string | null;
}

export default function UpdateStripeApiDialog(props: { onClose: () => void; apiKey: ApiKeyInput }) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  // GraphQL mutation hook
  const [updateStripeApi, { error, loading }] = useUpdateStripeApi();
  const [validate] = useValidateStripeAPIKey();

  const [deleteStripeApi] = useDeleteStripeApi();

  // State for form input
  const [apiKey, setApiKey] = useState<ApiKeyInput>({
    id: '',
    name: '',
    secretKey: '',
    publishableKey: '',
    webhookSecret: '',
  });

  const [validation, setValidation] = useState<Record<string, boolean>>({
    secretKey: false,
    publishableKey: false,
  });

  // Validation state
  const [emptyValidation, setEmptyValidation] = useState<Record<string, boolean>>({
    name: false,
    secretKey: false,
    publishableKey: false,
    webhookSecret: false,
  });

  // Populate initial data for the dialog when props change
  useEffect(() => {
    setApiKey(props.apiKey);
  }, [props.apiKey]);

  // Validation function
  const isPassValidation = () => {
    let isValid = true;
    const validations: Record<string, boolean> = {};

    Object.keys(apiKey).forEach((key) => {
      if ((apiKey as never)[key] === '' && key !== 'id' && key !== 'webhookSecret') {
        validations[key] = true;
        isValid = false;
      }
    });

    setEmptyValidation(validations);
    return isValid;
  };

  const validateSubmitForm = async (): Promise<boolean> => {
    try {
      const result = await validate({
        variables: {
          publishableKey: apiKey.publishableKey,
          secretKey: apiKey.secretKey,
        },
      });

      const queryValidations: Record<string, boolean> = {
        secretKey: false,
        publishableKey: false,
      };

      if (result.data?.validateStripeAPIKey.isValid) {
        return true;
      }

      if (result.data?.validateStripeAPIKey.details.secretKey) {
        queryValidations.secretKey = true;
      }

      if (result.data?.validateStripeAPIKey.details.publishableKey) {
        queryValidations.publishableKey = true;
      }

      setValidation(queryValidations);
      return false;
    } catch (error) {
      showAlert({
        status: 'error',
        message: t('stripe.apiKey.validation.error.message', 'Failed to validate API keys'),
        title: t('stripe.apiKey.validation.error.title', 'Validation Error'),
      });
      return false;
    }
  };

  // Submit handler
  const onSubmit = async () => {
    if (!isPassValidation()) return;

    // First validate the API keys
    const isValid = await validateSubmitForm();
    if (!isValid) {
      return;
    }

    updateStripeApi({
      variables: {
        id: apiKey.id,
        input: {
          name: apiKey.name.trim(),
          secretKey: apiKey.secretKey.trim(),
          publishableKey: apiKey.publishableKey.trim(),
          webhookSecret: apiKey.webhookSecret ? apiKey.webhookSecret.trim() : null,
        },
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          message: t('stripe.apiKey.update.success.message', 'Successfully updated API Key'),
          title: t('stripe.apiKey.update.success.title', 'Success'),
        });
        props.onClose();
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          message: e.message,
          title: t('stripe.apiKey.update.error.title', 'Error'),
        });
      });
  };

  // Delete
  const onDelete = () => {
    deleteStripeApi({
      variables: {
        id: apiKey.id,
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          message: t('stripe.apiKey.delete.success.message', 'Successfully deleted API Key'),
          title: t('stripe.apiKey.delete.success.title', 'Success'),
        });
        props.onClose();
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          message: e.message,
          title: t('stripe.apiKey.delete.error.title', 'Error'),
        });
      });
  };

  const clearWebhook = () => {
    setApiKey({
      ...apiKey,
      webhookSecret: '',
    });
  };

  return (
    <Card>
      <CardHeader title={t('stripe.apiKey.update.header', 'Update API Key')} onCrossButton={props.onClose} />

      {/* Error view */}
      <ApplicationErrorView error={error} />

      <CardContent>
        <FormLayout>
          <FormItem title={t('stripe.apiKey.name.title', 'Name')}>
            <TextInput
              type="text"
              label="name"
              value={apiKey.name}
              placeholder={t('stripe.apiKey.name.placeholder', 'Enter name')}
              name="name"
              error={emptyValidation.name ? t('stripe.apiKey.name.error', 'Name is required') : undefined}
              onChange={(e) => {
                setApiKey((prev) => ({
                  ...prev,
                  name: e.target.value,
                }));
              }}
            />
          </FormItem>

          <FormItem title={t('stripe.apiKey.secretKey.title', 'Secret Key')}>
            <TextInput
              type="text"
              label="secretKey"
              value={apiKey.secretKey}
              placeholder={t('stripe.apiKey.secretKey.placeholder', 'Enter secret key')}
              name="secretKey"
              error={
                emptyValidation.secretKey
                  ? t('stripe.apiKey.secretKey.error', 'Secret key is required')
                  : validation.secretKey
                    ? t('stripe.apiKey.secretKey.notvalid', 'secretKey not valid')
                    : undefined
              }
              onChange={(e) => {
                setApiKey((prev) => ({
                  ...prev,
                  secretKey: e.target.value,
                }));
              }}
            />
          </FormItem>

          <FormItem title={t('stripe.apiKey.publishableKey.title', 'Publishable Key')}>
            <TextInput
              type="text"
              label="publishableKey"
              value={apiKey.publishableKey}
              placeholder={t('stripe.apiKey.publishableKey.placeholder', 'Enter publishable key')}
              name="publishableKey"
              error={
                emptyValidation.publishableKey
                  ? t('stripe.apiKey.publishableKey.error', 'Publishable key is required')
                  : validation.publishableKey
                    ? t('stripe.apiKey.publishableKey.notvalid', 'publishableKey not valid')
                    : undefined
              }
              onChange={(e) => {
                setApiKey((prev) => ({
                  ...prev,
                  publishableKey: e.target.value,
                }));
              }}
            />
          </FormItem>

          <FormItem
            title={t('stripe.apiKey.webhook-secretKey.title', 'Webhook Secret Key')}
            subtitle={t('common.optional', '(Optional)')}
          >
            <div className="flex space-x-2 w-full">
              <TextInput
                type="text"
                label="webhookSecret"
                className="grow"
                value={apiKey.webhookSecret}
                placeholder={t('stripe.apiKey.webhook-secretKey.placeholder', 'Enter webhook secret key')}
                name="webhookSecret"
                onChange={(e) => {
                  setApiKey((prev) => ({
                    ...prev,
                    webhookSecret: e.target.value,
                  }));
                }}
              />
              <SecondaryButton onClick={clearWebhook}>{t('common.api.clear', 'Clear')}</SecondaryButton>
            </div>
          </FormItem>
        </FormLayout>
      </CardContent>

      {/* Footer actions */}
      <CardFooter>
        <div className="flex justify-between items-center w-full">
          <DestroyButton onClick={onDelete}>{t('common.button.delete', 'Delete')}</DestroyButton>

          <div className="flex justify-end space-x-2">
            <SecondaryButton onClick={props.onClose}>{t('stripe.apiKey.cancel', 'Cancel')}</SecondaryButton>
            <PrimaryButton
              onClick={() => {
                void onSubmit();
              }}
              loading={loading}
            >
              {t('stripe.apiKey.update', 'Update')}
            </PrimaryButton>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
